import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { TRIAL_DAYS, WORKSPACE_ADDON_PRICE } from "@/lib/tier";

const FAQ: { q: string; a: ReactNode }[] = [
  {
    q: "How does the 10-day free trial work?",
    a: "Every plan starts with a 10-day free trial. We collect your card at signup so we can confirm you're a real person, but nothing is charged until day 11. Cancel anytime during the trial and you won't be billed.",
  },
  {
    q: "Why do you need a card if it's a free trial?",
    a: "To keep automated signups and abuse off the platform. The card is verified, not charged, until your trial ends.",
  },
  {
    q: "Can I switch plans later?",
    a: <>Yes — upgrade, downgrade, or cancel anytime from your <Link to="/settings" className="text-primary underline">account settings</Link>.</>,
  },
  {
    q: "What's a workspace, and when do I need more than one?",
    a: "A workspace is a self-contained library + excerpts + Composer + connectors. Every plan includes Composer; higher tiers add more workspaces, premium AI, and library isolation for client work.",
  },
  {
    q: "What does BYOK AI mean?",
    a: "Bring Your Own Key. Elite and Agency connect their own premium-AI provider key, so inference costs and limits live with your provider account.",
  },
  {
    q: "Is my data used to train AI models?",
    a: "No. Your excerpts, library, and drafts are never used to train models — ours or anyone else's.",
  },
  {
    q: "What's the workspace add-on?",
    a: `An extra workspace beyond what your plan includes, billed at $${WORKSPACE_ADDON_PRICE.monthly}/month. Available on plans that allow add-ons.`,
  },
  {
    q: "Do you offer refunds?",
    a: <>We do not offer refunds. Every plan includes a {TRIAL_DAYS}-day free trial so you can make sure Excerptus is a good fit — cancel anytime within the trial and you won't be charged. After the trial, you can cancel anytime and keep access through the end of your current billing period. See <Link to="/terms/" className="text-primary underline">Terms</Link>.</>,
  },
  {
    q: "How does branding work on excerpt images?",
    a: <>The Excerptus corner mark appears by default on every plan. Upload your own logo in the <Link to="/image-studio" className="text-primary underline">Image Studio</Link> to replace it.</>,
  },
  {
    q: "Can I export data or close my account?",
    a: <>Yes — signed-in users can download a JSON workspace export from <Link to="/settings" className="text-primary underline">Settings</Link>. Workspace deletion and billing cancellation are also managed there; full account closure is handled by email so required billing records can be retained safely.</>,
  },
];

export const PricingFAQ = () => (
  <Accordion type="single" collapsible className="w-full">
    {FAQ.map((item, i) => (
      <AccordionItem key={item.q} value={`item-${i}`}>
        <AccordionTrigger className="text-left">{item.q}</AccordionTrigger>
        <AccordionContent className="text-muted-foreground">{item.a}</AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

/**
 * Plain-text answers mirroring the FAQ above, used to emit FAQPage JSON-LD.
 * Keep wording in sync with the rich answers — JSX links are flattened to
 * descriptive text since structured data must be plain strings.
 */
const FAQ_PLAIN: { q: string; a: string }[] = [
  {
    q: "How does the 10-day free trial work?",
    a: "Every plan starts with a 10-day free trial. We collect your card at signup so we can confirm you're a real person, but nothing is charged until day 11. Cancel anytime during the trial and you won't be billed.",
  },
  {
    q: "Why do you need a card if it's a free trial?",
    a: "To keep automated signups and abuse off the platform. The card is verified, not charged, until your trial ends.",
  },
  {
    q: "Can I switch plans later?",
    a: "Yes — upgrade, downgrade, or cancel anytime from your account settings.",
  },
  {
    q: "What's a workspace, and when do I need more than one?",
    a: "A workspace is a self-contained library + excerpts + Composer + connectors. Every plan includes Composer; higher tiers add more workspaces, premium AI, and library isolation for client work.",
  },
  {
    q: "What does BYOK AI mean?",
    a: "Bring Your Own Key. Elite and Agency connect their own premium-AI provider key, so inference costs and limits live with your provider account.",
  },
  {
    q: "Is my data used to train AI models?",
    a: "No. Your excerpts, library, and drafts are never used to train models — ours or anyone else's.",
  },
  {
    q: "What's the workspace add-on?",
    a: `An extra workspace beyond what your plan includes, billed at $${WORKSPACE_ADDON_PRICE.monthly}/month. Available on plans that allow add-ons.`,
  },
  {
    q: "Do you offer refunds?",
    a: `We do not offer refunds. Every plan includes a ${TRIAL_DAYS}-day free trial so you can make sure Excerptus is a good fit — cancel anytime within the trial and you won't be charged. After the trial, you can cancel anytime and keep access through the end of your current billing period.`,
  },
  {
    q: "How does branding work on excerpt images?",
    a: "The Excerptus corner mark appears by default on every plan. Upload your own logo in the Image Studio to replace it.",
  },
  {
    q: "Can I export data or close my account?",
    a: "Yes — signed-in users can download a JSON workspace export from Settings. Workspace deletion and billing cancellation are also managed there; full account closure is handled by email so required billing records can be retained safely.",
  },
];

export const PricingFAQJsonLd = () => {
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_PLAIN.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
};
